import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Building2, MapPin, Plus, Layers, FileUp } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Card } from "src/components/ui/card"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"

import { useProject } from "src/features/projects/hooks/useProjects"
import { useUnits, useChangeUnitStatus } from "src/features/projects/hooks/useUnits"
import { updateDeal } from "src/features/deals/api/dealsApi"
import { fetchTowers } from "src/features/projects/api/projectsApi"
import { AvailabilityGrid } from "src/features/projects/components/AvailabilityGrid"
import { UnitStatusDialog } from "src/features/projects/components/UnitStatusDialog"
import { UNIT_STATUS_STYLES } from "src/features/projects/components/UnitStatusBadge"
import { TowerFormDrawer } from "src/features/projects/components/TowerFormDrawer"
import { UnitFormDrawer } from "src/features/projects/components/UnitFormDrawer"
import { ImportUnitsDrawer } from "src/features/projects/components/ImportUnitsDrawer"
import { ProjectActivityTabs } from "src/features/projects/components/ProjectActivityTabs"
import { DocumentList } from "src/features/documents/components/DocumentList"
import { CATEGORY_GROUPS } from "src/features/documents/constants"
import { ProjectSiteVisitGeofence } from "src/features/site_visits/components/ProjectSiteVisitGeofence"
import { PROJECT_TYPE_MAP, LIGHT_CARD } from "src/features/projects/constants"

// Poll every 15s so blocks/unblocks made by other users surface without a
// websocket. This is the seam where AnyCable could replace polling later.
const POLL_MS = 15000

function SummaryCard({ status, count }) {
  const style = UNIT_STATUS_STYLES[status]
  return (
    <Card className={`flex items-center gap-3 p-4 ${LIGHT_CARD}`}>
      <span
        className="h-9 w-9 shrink-0 rounded-lg opacity-90"
        style={{ backgroundColor: style.swatchColor }}
      />
      <div>
        <p className="text-xl font-semibold text-slate-900">{count}</p>
        <p className="text-[12px] text-slate-500">{style.label}</p>
      </div>
    </Card>
  )
}

export default function ProjectDashboardPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data: projectData, loading: projectLoading, fetch: fetchProject } = useProject()
  const { units, loading: unitsLoading, fetch: fetchUnitsList, refresh } = useUnits(id, { pollMs: POLL_MS })
  const changeStatus = useChangeUnitStatus()

  const [towers, setTowers] = useState([])
  const [statusFilter, setStatusFilter] = useState("")
  const [dialogUnit, setDialogUnit] = useState(null)
  const [towerDrawer, setTowerDrawer] = useState(false)
  const [editingTower, setEditingTower] = useState(null)
  const [unitDrawer, setUnitDrawer] = useState(false)
  const [editingUnit, setEditingUnit] = useState(null)
  const [importDrawer, setImportDrawer] = useState(false)

  const loadTowers = useCallback(() => {
    if (!id) return
    fetchTowers(id).then((d) => setTowers(d.towers || [])).catch(() => {})
  }, [id])

  useEffect(() => { if (id) fetchProject(id) }, [id, fetchProject])
  useEffect(() => { if (id) fetchUnitsList({ status: statusFilter }) }, [id, statusFilter, fetchUnitsList])
  useEffect(() => { loadTowers() }, [loadTowers])

  // Refresh both the unit grid and the project summary after a mutation.
  const refreshAll = useCallback(() => {
    refresh()
    if (id) fetchProject(id)
  }, [refresh, id, fetchProject])

  const project = projectData?.project

  // Live counts derived from the (polled) unit list so the summary stays in sync.
  const counts = useMemo(() => {
    const c = { available: 0, blocked: 0, booked: 0, sold: 0 }
    for (const u of units) if (c[u.status] != null) c[u.status] += 1
    return c
  }, [units])

  async function handleConfirm({ status, reason, dealId }) {
    try {
      await changeStatus.mutate(id, dialogUnit.id, { status, reason })
      // Link the chosen deal to this unit so the block/booking is traceable to
      // the opportunity it's held for. Best-effort: the status change already
      // succeeded, so we don't fail the whole action if the link fails.
      if (dealId) {
        try {
          await updateDeal(dealId, { unit_id: dialogUnit.id })
        } catch {
          toast({ variant: "destructive", title: "Unit updated, but deal link failed", description: "The unit's status changed but it couldn't be linked to the deal." })
        }
      }
      toast({ title: "Unit updated", description: `${dialogUnit.unit_number} → ${UNIT_STATUS_STYLES[status].label}` })
      setDialogUnit(null)
      refresh()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : (errors?.message || "Something went wrong")
      toast({ variant: "destructive", title: "Could not update unit", description: msg })
    }
  }

  return (
    <>
      <div className="space-y-5 px-8 py-7">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 -ml-2 text-slate-500"
          onClick={() => navigate("/projects")}
        >
          <ArrowLeft className="h-4 w-4" />
          All projects
        </Button>

        {/* ── Header ── */}
        {projectLoading && !project ? (
          <Skeleton className="h-16 w-full max-w-md rounded-xl" />
        ) : project ? (
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
                <Building2 className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-slate-900">{project.name}</h1>
                <p className="flex items-center gap-1.5 text-[13px] text-slate-500">
                  {project.code && <span className="font-mono uppercase">{project.code}</span>}
                  {(project.city || project.state) && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {[project.city, project.state].filter(Boolean).join(", ")}
                    </span>
                  )}
                  {PROJECT_TYPE_MAP[project.project_type] && (
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${PROJECT_TYPE_MAP[project.project_type].badge}`}>
                      {PROJECT_TYPE_MAP[project.project_type].label}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setEditingTower(null); setTowerDrawer(true) }}>
                <Layers className="h-3.5 w-3.5" />
                Add tower
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setImportDrawer(true)}>
                <FileUp className="h-3.5 w-3.5" />
                Import units
              </Button>
              <Button variant="default" size="sm" className="gap-1.5" onClick={() => { setEditingUnit(null); setUnitDrawer(true) }}>
                <Plus className="h-3.5 w-3.5" />
                Add unit
              </Button>
            </div>
          </div>
        ) : null}

        {/* ── Availability summary ── */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Object.keys(UNIT_STATUS_STYLES).map((s) => (
            <SummaryCard key={s} status={s} count={counts[s]} />
          ))}
        </div>

        {/* ── Filter chips ── */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStatusFilter("")}
            className={[
              "h-auto rounded-full border px-3 py-1 text-[13px] font-medium transition-colors",
              statusFilter === "" ? "border-slate-800 bg-slate-800 text-white hover:bg-slate-700 hover:text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50",
            ].join(" ")}
          >
            All
          </Button>
          {Object.entries(UNIT_STATUS_STYLES).map(([s, style]) => (
            <Button
              key={s}
              type="button"
              variant="outline"
              onClick={() => setStatusFilter(s)}
              className={[
                "h-auto rounded-full border px-3 py-1 text-[13px] font-medium transition-colors",
                statusFilter === s ? "border-slate-800 bg-slate-800 text-white hover:bg-slate-700 hover:text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50",
              ].join(" ")}
            >
              {style.label}
            </Button>
          ))}
        </div>

        {/* ── Grid ── */}
        <Card className={`p-5 ${LIGHT_CARD}`}>
          {unitsLoading && units.length === 0 ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-2">
              {Array.from({ length: 18 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square w-full rounded-lg" />
              ))}
            </div>
          ) : units.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Building2 className="h-9 w-9 text-slate-300" />
              <p className="font-medium text-slate-700">No units{statusFilter ? ` with status “${UNIT_STATUS_STYLES[statusFilter].label}”` : " yet"}</p>
              <p className="text-[13px] text-slate-500">Units added to this project will appear here.</p>
            </div>
          ) : (
            <AvailabilityGrid
              units={units}
              towers={towers}
              onUnitClick={setDialogUnit}
              onEditTower={(tower) => { setEditingTower(tower); setTowerDrawer(true) }}
            />
          )}
        </Card>

        {/* ── Site visit geofence (the check-in zone for this project) ── */}
        <ProjectSiteVisitGeofence projectId={id} project={project} className={LIGHT_CARD} />

        {/* ── Sales activity (leads / deals / bookings for this project) ── */}
        <ProjectActivityTabs projectId={id} />

        {/* ── Project documents (legal, approvals, completion, marketing) ── */}
        <Card className={`p-5 ${LIGHT_CARD}`}>
          <DocumentList scope={{ type: "project", id }} categories={CATEGORY_GROUPS.project} />
        </Card>
      </div>

      <UnitStatusDialog
        open={!!dialogUnit}
        unit={dialogUnit}
        onClose={() => setDialogUnit(null)}
        onConfirm={handleConfirm}
        onEdit={(unit) => { setDialogUnit(null); setEditingUnit(unit); setUnitDrawer(true) }}
        isPending={changeStatus.isPending}
      />

      <TowerFormDrawer
        open={towerDrawer}
        projectId={id}
        tower={editingTower}
        onClose={() => { setTowerDrawer(false); setEditingTower(null) }}
        onSuccess={loadTowers}
      />

      <UnitFormDrawer
        open={unitDrawer}
        projectId={id}
        unit={editingUnit}
        towers={towers}
        onClose={() => { setUnitDrawer(false); setEditingUnit(null) }}
        onSuccess={refreshAll}
      />

      <ImportUnitsDrawer
        open={importDrawer}
        projectId={id}
        onClose={() => setImportDrawer(false)}
        onSuccess={() => { refreshAll(); loadTowers() }}
      />
    </>
  )
}
